import "./styles.css";
import translations from "@/config/translations";
import useLangStore from "@/hooks/useLangStore";
import { useState } from "react";

export interface GoogleMapsUrlPopupDetails {
    onSuccess: (location: { lat: number; lng: number }) => void;
}

interface Props {
    onClose: () => void;
    details?: GoogleMapsUrlPopupDetails;
}

// ── Parser ────────────────────────────────────────────────────────────────────

function isValidCoord(lat: number, lng: number): boolean {
    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && (lat !== 0 || lng !== 0);
}

function parseGoogleMapsUrl(url: string): { lat: number; lng: number } | null {
    // !3dlat!4dlng — exact pin coordinates embedded in /data= segment.
    // Must be checked BEFORE @lat,lng because @lat,lng is only the viewport
    // center and can shift significantly from the actual pin location.
    // Use the LAST match: when a URL has multiple places (search results,
    // multi-branch) Google encodes the selected/highlighted place last.
    const latMatches = [...url.matchAll(/!3d(-?\d+\.?\d*)/g)];
    const lngMatches = [...url.matchAll(/!4d(-?\d+\.?\d*)/g)];
    if (latMatches.length > 0 && lngMatches.length > 0) {
        const lat = parseFloat(latMatches[latMatches.length - 1][1]);
        const lng = parseFloat(lngMatches[lngMatches.length - 1][1]);
        if (isValidCoord(lat, lng)) return { lat, lng };
    }

    // Query-string formats (q, ll, query, center) — also more precise than viewport
    try {
        const u = new URL(url);
        for (const param of ["q", "ll", "query", "center"]) {
            const val = u.searchParams.get(param);
            if (val) {
                const m = val.match(/^(-?\d+\.?\d*),(-?\d+\.?\d*)$/);
                if (m) {
                    const lat = parseFloat(m[1]);
                    const lng = parseFloat(m[2]);
                    if (isValidCoord(lat, lng)) return { lat, lng };
                }
            }
        }
    } catch {
        // not a parseable URL — fall through
    }

    // @lat,lng — viewport center, least precise, used as last resort
    const atMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (atMatch) {
        const lat = parseFloat(atMatch[1]);
        const lng = parseFloat(atMatch[2]);
        if (isValidCoord(lat, lng)) return { lat, lng };
    }

    return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function GoogleMapsUrlPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const f = t.branchForm;
    const dir = lang === "ar" ? "rtl" : "ltr";

    const [url, setUrl] = useState("");
    const [error, setError] = useState<"short" | "invalid" | null>(null);

    const isShortUrl = (val: string) => /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(val);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = url.trim();
        if (isShortUrl(trimmed)) {
            setError("short");
            return;
        }
        const result = parseGoogleMapsUrl(trimmed);
        if (!result) {
            setError("invalid");
            return;
        }
        details?.onSuccess(result);
        onClose();
    };

    return (
        <div className="gmu-popup" dir={dir}>
            <div className="gmu-header">
                <h2 className="gmu-title">{f.googleMapsPopupTitle}</h2>
                <button type="button" className="gmu-close" onClick={onClose}>
                    ×
                </button>
            </div>

            <form className="gmu-body" onSubmit={handleSubmit} noValidate>
                <div className="gmu-fields">
                    <div className="gmu-field">
                        <label className="gmu-label">{f.googleMapsUrlLabel}</label>
                        <input
                            className={`gmu-input${error ? " gmu-input--error" : ""}`}
                            type="url"
                            value={url}
                            onChange={(e) => {
                                setUrl(e.target.value);
                                if (error) setError(null);
                            }}
                            placeholder="https://www.google.com/maps/..."
                            dir="ltr"
                            autoFocus
                        />
                        {error === "short" && <p className="gmu-error">{f.googleMapsUrlShort}</p>}
                        {error === "invalid" && <p className="gmu-error">{f.googleMapsUrlInvalid}</p>}
                        {!error && <p className="gmu-hint">{f.googleMapsUrlHint}</p>}
                    </div>
                </div>

                <div className="gmu-footer">
                    <button type="button" className="gmu-btn gmu-btn--cancel" onClick={onClose}>
                        {f.googleMapsCancel}
                    </button>
                    <button type="submit" className="gmu-btn gmu-btn--submit" disabled={!url.trim()}>
                        {f.googleMapsSubmit}
                    </button>
                </div>
            </form>
        </div>
    );
}
